import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound, ShieldCheck, ShieldOff, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";
import { useAdmin } from "../../context/AdminContext";

function ConfirmDialog({ message, onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="bg-nova-surface border border-white/10 rounded-2xl p-6 max-w-sm w-full space-y-4">
                <p className="text-white text-sm">{message}</p>
                <div className="flex gap-3 justify-end">
                    <button onClick={onCancel} className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white text-sm transition-colors">Zrušiť</button>
                    <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-400 transition-colors">Potvrdiť</button>
                </div>
            </div>
        </div>
    );
}

function ChangePasswordModal({ username, onClose }) {
    const [pw, setPw]       = useState("");
    const [show, setShow]   = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 6) { toast.error("Heslo musí mať aspoň 6 znakov"); return; }
        setLoading(true);
        try {
            await api.changeUserPassword(username, pw);
            toast.success("Heslo zmenené");
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="bg-nova-surface border border-white/10 rounded-2xl p-6 max-w-sm w-full space-y-4">
                <h3 className="text-white font-semibold">Zmeniť heslo — <span className="text-nova-cyan">{username}</span></h3>
                <form onSubmit={submit} className="space-y-3">
                    <div className="relative">
                        <input
                            type={show ? "text" : "password"}
                            value={pw}
                            onChange={e => setPw(e.target.value)}
                            placeholder="Nové heslo"
                            className="w-full h-10 px-3 pr-10 rounded-lg bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60"
                            autoFocus
                        />
                        <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="flex gap-3 justify-end">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white text-sm">Zrušiť</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-nova-cyan text-black font-semibold text-sm disabled:opacity-60">
                            {loading ? "Ukladám…" : "Uložiť"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminUsers() {
    const { user: me } = useAdmin();
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm]       = useState({ username: "", password: "", role: "admin" });
    const [showPw, setShowPw]   = useState(false);
    const [adding, setAdding]   = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [changePw, setChangePw] = useState(null);

    const load = () => api.listUsers().then(setUsers).catch(() => toast.error("Nepodarilo sa načítať používateľov")).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const addUser = async (e) => {
        e.preventDefault();
        if (!form.username.trim()) { toast.error("Zadajte meno používateľa"); return; }
        if (form.password.length < 6) { toast.error("Heslo musí mať aspoň 6 znakov"); return; }
        setAdding(true);
        try {
            await api.createUser(form.username.trim(), form.password, form.role);
            toast.success("Používateľ vytvorený");
            setForm({ username: "", password: "", role: "admin" });
            load();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setAdding(false);
        }
    };

    const deleteUser = async (username) => {
        try {
            await api.deleteUser(username);
            toast.success("Používateľ zmazaný");
            setConfirm(null);
            load();
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <div className="max-w-2xl space-y-6">
            {confirm && (
                <ConfirmDialog
                    message={`Naozaj chcete zmazať používateľa „${confirm}"?`}
                    onConfirm={() => deleteUser(confirm)}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {changePw && (
                <ChangePasswordModal username={changePw} onClose={() => { setChangePw(null); }} />
            )}

            <div className="mb-8">
                <h1 className="font-heading text-2xl font-black text-white tracking-tight">Správa používateľov</h1>
                <p className="text-zinc-500 text-sm mt-1">Administrátorské účty pre prístup do admin panelu.</p>
            </div>

            {/* User list */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-white/8">
                    <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan">/ Používatelia</h2>
                </div>
                {loading ? (
                    <div className="p-6 space-y-3 animate-pulse">
                        {[1,2].map(n => <div key={n} className="h-12 bg-nova-surface/40 rounded-lg" />)}
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {users.map(u => (
                            <div key={u.username} className="flex items-center justify-between px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-nova-cyan/10 border border-nova-cyan/20 flex items-center justify-center">
                                        <span className="text-nova-cyan text-xs font-bold uppercase">{u.username[0]}</span>
                                    </div>
                                    <div>
                                        <div className="text-white text-sm font-medium flex items-center gap-2">
                                            {u.username}
                                            {u.username === me?.username && (
                                                <span className="text-[9px] font-mono uppercase tracking-wider bg-nova-cyan/10 text-nova-cyan px-1.5 py-0.5 rounded">Vy</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                            <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${u.role === "superadmin" ? "bg-nova-purple/20 text-nova-purple border border-nova-purple/30" : "bg-white/5 text-zinc-500 border border-white/8"}`}>
                                                {u.role || "admin"}
                                            </span>
                                            {u.totp_enabled
                                                ? <><ShieldCheck className="w-3 h-3 text-green-400" /><span className="text-[10px] text-green-400 font-mono">2FA</span></>
                                                : <span className="text-[10px] text-zinc-600 font-mono">no 2FA</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setChangePw(u.username)}
                                        className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
                                        title="Zmeniť heslo"
                                    >
                                        <KeyRound className="w-4 h-4" />
                                    </button>
                                    {u.username !== me?.username && (
                                        <button
                                            onClick={() => setConfirm(u.username)}
                                            className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-400/5 transition-colors"
                                            title="Zmazať"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {users.length === 0 && (
                            <div className="px-5 py-8 text-center text-zinc-600 text-sm">Žiadni používatelia</div>
                        )}
                    </div>
                )}
            </div>

            {/* Add new user */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan mb-5">/ Nový používateľ</h2>
                <form onSubmit={addUser} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Meno</label>
                            <input
                                value={form.username}
                                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                                placeholder="jan.novak"
                                className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Heslo</label>
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    value={form.password}
                                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                    placeholder="min. 6 znakov"
                                    className="w-full h-10 px-3 pr-9 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                                />
                                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                                    {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Rola</label>
                        <select
                            value={form.role}
                            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                            className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                        >
                            <option value="admin">Admin</option>
                            <option value="superadmin">Superadmin</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={adding}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors disabled:opacity-50"
                    >
                        <UserPlus className="w-4 h-4" />
                        {adding ? "Vytváram…" : "Vytvoriť používateľa"}
                    </button>
                </form>
            </div>
        </div>
    );
}
